import React, { useState, useEffect, useCallback } from 'react';
import { RefreshCw, Plus, AlertTriangle } from 'lucide-react';
import { assetInventoryApi, Asset, AssetStats } from '../api/assetInventory.api';
import PageInfoButton from '@/components/PageInfoButton';

const classificationColors: Record<string, string> = {
  public: 'bg-green-100 text-green-800',
  internal: 'bg-blue-100 text-blue-800',
  confidential: 'bg-yellow-100 text-yellow-800',
  restricted: 'bg-red-100 text-red-800',
};

const criticalityColors: Record<string, string> = {
  low: 'bg-green-100 text-green-800',
  medium: 'bg-yellow-100 text-yellow-800',
  high: 'bg-orange-100 text-orange-800',
  critical: 'bg-red-100 text-red-800',
};

const statusColors: Record<string, string> = {
  active: 'bg-green-100 text-green-800',
  under_review: 'bg-yellow-100 text-yellow-800',
  decommissioned: 'bg-gray-100 text-gray-600',
  disposed: 'bg-red-100 text-red-700',
};

const ASSET_TYPES = ['hardware', 'software', 'data', 'service', 'facility', 'personnel'];
const CLASSIFICATIONS = ['public', 'internal', 'confidential', 'restricted'];
const CRITICALITIES = ['low', 'medium', 'high', 'critical'];

const EMPTY_FORM: Partial<Asset> = {
  name: '',
  asset_type: 'hardware' as any,
  sub_type: '',
  description: '',
  classification: 'internal' as any,
  criticality: 'medium' as any,
  department: '',
  location: '',
  serial_number: '',
  license_info: '',
  next_review_date: '',
};

const AssetInventory: React.FC = () => {
  const [assets, setAssets] = useState<Asset[]>([]);
  const [stats, setStats] = useState<AssetStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);

  const [assetTypeFilter, setAssetTypeFilter] = useState('');
  const [classificationFilter, setClassificationFilter] = useState('');
  const [criticalityFilter, setCriticalityFilter] = useState('');
  const [search, setSearch] = useState('');

  const [createModal, setCreateModal] = useState(false);
  const [editAsset, setEditAsset] = useState<Asset | null>(null);
  const [form, setForm] = useState<Partial<Asset>>({ ...EMPTY_FORM });
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    setIsLoading(true);
    setError('');
    try {
      const [res, statsRes] = await Promise.all([
        assetInventoryApi.getAssets({
          ...(assetTypeFilter && { asset_type: assetTypeFilter }),
          ...(classificationFilter && { classification: classificationFilter }),
          ...(criticalityFilter && { criticality: criticalityFilter }),
          ...(search && { search }),
          page,
          limit: 20,
        }),
        assetInventoryApi.getStats(),
      ]);
      setAssets(res.assets || []);
      setTotal(res.total || 0);
      setStats(statsRes);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setIsLoading(false);
    }
  }, [assetTypeFilter, classificationFilter, criticalityFilter, search, page]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setForm({ ...EMPTY_FORM }); setEditAsset(null); setCreateModal(true); };
  const openEdit = (a: Asset) => {
    setForm({
      name: a.name, asset_type: a.asset_type, sub_type: a.sub_type || '',
      description: a.description || '', classification: a.classification,
      criticality: a.criticality, department: a.department || '',
      location: a.location || '', serial_number: a.serial_number || '',
      license_info: a.license_info || '', next_review_date: a.next_review_date || '',
    });
    setEditAsset(a);
    setCreateModal(true);
  };

  const handleSave = async () => {
    if (!form.name || !form.asset_type || !form.classification || !form.criticality) {
      alert('Name, type, classification and criticality are required');
      return;
    }
    setSubmitting(true);
    try {
      if (editAsset) {
        await assetInventoryApi.updateAsset(editAsset.id, form);
      } else {
        await assetInventoryApi.createAsset(form);
      }
      setCreateModal(false);
      load();
    } catch (e: any) {
      alert(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Delete this asset?')) return;
    try {
      await assetInventoryApi.deleteAsset(id);
      load();
    } catch (e: any) {
      alert(e.message);
    }
  };

  if (isLoading) return (
    <div className="flex items-center justify-center h-64">
      <RefreshCw className="w-6 h-6 animate-spin text-blue-600" />
      <span className="ml-2 text-slate-600">Loading...</span>
    </div>
  );

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-1"><h1 className="text-3xl font-bold text-slate-900">Asset Inventory</h1><PageInfoButton /></div>
          <p className="text-slate-500 text-sm mt-1">Track and classify all organisational assets</p>
        </div>
        <div className="flex gap-2">
          <button onClick={load} className="flex items-center gap-1 px-3 py-2 text-sm text-slate-600 border rounded-lg hover:bg-slate-50">
            <RefreshCw className="w-4 h-4" /> Refresh
          </button>
          <button onClick={openCreate} className="flex items-center gap-1 px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700">
            <Plus className="w-4 h-4" /> Add Asset
          </button>
        </div>
      </div>

      {error && <div className="bg-red-50 text-red-700 px-4 py-3 rounded-lg text-sm">{error}</div>}

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white border rounded-xl p-4">
            <div className="text-3xl font-bold text-slate-900">{stats.total}</div>
            <div className="text-xs text-slate-500 mt-1">Total Assets</div>
          </div>
          <div className="bg-white border rounded-xl p-4">
            <div className="text-2xl font-bold text-red-600">{stats.high_critical_count}</div>
            <div className="text-xs text-slate-500 mt-1">High / Critical</div>
          </div>
          <div className="bg-white border rounded-xl p-4">
            <div className="flex gap-1 flex-wrap">
              {CLASSIFICATIONS.map(c => (
                <span key={c} className={`text-xs px-1.5 py-0.5 rounded ${classificationColors[c]}`}>
                  {c}: {(stats.by_classification as any)[c] || 0}
                </span>
              ))}
            </div>
            <div className="text-xs text-slate-500 mt-1">By Classification</div>
          </div>
          <div className="bg-white border rounded-xl p-4 flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-yellow-500" />
            <div>
              <div className="text-xl font-bold text-slate-900">{stats.due_for_review}</div>
              <div className="text-xs text-slate-500">Due for Review</div>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap gap-3">
        <input
          value={search}
          onChange={e => { setSearch(e.target.value); setPage(1); }}
          placeholder="Search assets..."
          className="border rounded-lg px-3 py-2 text-sm w-48 focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
        <select value={assetTypeFilter} onChange={e => { setAssetTypeFilter(e.target.value); setPage(1); }}
          className="border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
          <option value="">All Types</option>
          {ASSET_TYPES.map(t => <option key={t} value={t} className="capitalize">{t}</option>)}
        </select>
        <select value={classificationFilter} onChange={e => { setClassificationFilter(e.target.value); setPage(1); }}
          className="border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
          <option value="">All Classifications</option>
          {CLASSIFICATIONS.map(c => <option key={c} value={c} className="capitalize">{c}</option>)}
        </select>
        <select value={criticalityFilter} onChange={e => { setCriticalityFilter(e.target.value); setPage(1); }}
          className="border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
          <option value="">All Criticality</option>
          {CRITICALITIES.map(c => <option key={c} value={c} className="capitalize">{c}</option>)}
        </select>
      </div>

      {/* Table */}
      <div className="bg-white border rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50">
            <tr>
              {['Name', 'Type', 'Classification', 'Criticality', 'Status', 'Department', 'Owner', 'Review Date', 'Actions'].map(h => (
                <th key={h} className="px-4 py-3 text-left font-medium text-slate-600">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {assets.length === 0 ? (
              <tr><td colSpan={9} className="px-4 py-8 text-center text-slate-400">No assets found.</td></tr>
            ) : assets.map(a => (
              <tr key={a.id} className="hover:bg-slate-50">
                <td className="px-4 py-3 text-left">
                  <div className="font-medium text-slate-900">{a.name}</div>
                  {a.sub_type && <div className="text-xs text-slate-400">{a.sub_type}</div>}
                </td>
                <td className="px-4 py-3 text-left text-slate-600 capitalize">{a.asset_type}</td>
                <td className="px-4 py-3 text-left">
                  <span className={`text-xs px-2 py-1 rounded-full font-medium ${classificationColors[a.classification]}`}>
                    {a.classification}
                  </span>
                </td>
                <td className="px-4 py-3 text-left">
                  <span className={`text-xs px-2 py-1 rounded-full font-medium ${criticalityColors[a.criticality]}`}>
                    {a.criticality}
                  </span>
                </td>
                <td className="px-4 py-3 text-left">
                  <span className={`text-xs px-2 py-1 rounded-full ${statusColors[a.status]}`}>{a.status.replace('_', ' ')}</span>
                </td>
                <td className="px-4 py-3 text-left text-slate-500">{a.department || '—'}</td>
                <td className="px-4 py-3 text-left text-slate-500">{a.owner_name || '—'}</td>
                <td className="px-4 py-3 text-left text-slate-500">
                  {a.next_review_date ? new Date(a.next_review_date).toLocaleDateString() : '—'}
                </td>
                <td className="px-4 py-3 text-left">
                  <div className="flex gap-2">
                    <button onClick={() => openEdit(a)} className="text-xs px-2 py-1 border rounded hover:bg-slate-50 text-slate-600">Edit</button>
                    <button onClick={() => handleDelete(a.id)} className="text-xs px-2 py-1 border rounded hover:bg-red-50 text-red-600">Delete</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {total > 20 && (
          <div className="px-4 py-3 border-t flex items-center justify-between text-sm text-slate-500">
            <span>Showing {assets.length} of {total}</span>
            <div className="flex gap-2">
              <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
                className="px-3 py-1 border rounded hover:bg-slate-50 disabled:opacity-40">Previous</button>
              <button onClick={() => setPage(p => p + 1)} disabled={assets.length < 20}
                className="px-3 py-1 border rounded hover:bg-slate-50 disabled:opacity-40">Next</button>
            </div>
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      {createModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b">
              <h2 className="text-lg font-semibold text-slate-900">{editAsset ? 'Edit Asset' : 'Add Asset'}</h2>
            </div>
            <div className="px-6 py-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Name *</label>
                <input value={form.name || ''} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                  className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Asset Type *</label>
                  <select value={form.asset_type || 'hardware'} onChange={e => setForm(f => ({ ...f, asset_type: e.target.value as any }))}
                    className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                    {ASSET_TYPES.map(t => <option key={t} value={t} className="capitalize">{t}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Sub-type</label>
                  <input value={form.sub_type || ''} onChange={e => setForm(f => ({ ...f, sub_type: e.target.value }))}
                    placeholder="e.g. Laptop, Database..."
                    className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Classification *</label>
                  <select value={form.classification || 'internal'} onChange={e => setForm(f => ({ ...f, classification: e.target.value as any }))}
                    className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                    {CLASSIFICATIONS.map(c => <option key={c} value={c} className="capitalize">{c}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Criticality *</label>
                  <select value={form.criticality || 'medium'} onChange={e => setForm(f => ({ ...f, criticality: e.target.value as any }))}
                    className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                    {CRITICALITIES.map(c => <option key={c} value={c} className="capitalize">{c}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Description</label>
                <textarea value={form.description || ''} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                  rows={2} className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Department</label>
                  <input value={form.department || ''} onChange={e => setForm(f => ({ ...f, department: e.target.value }))}
                    className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Location</label>
                  <input value={form.location || ''} onChange={e => setForm(f => ({ ...f, location: e.target.value }))}
                    className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Serial Number</label>
                  <input value={form.serial_number || ''} onChange={e => setForm(f => ({ ...f, serial_number: e.target.value }))}
                    className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Next Review Date</label>
                  <input type="date" value={form.next_review_date || ''} onChange={e => setForm(f => ({ ...f, next_review_date: e.target.value }))}
                    className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">License Info</label>
                <input value={form.license_info || ''} onChange={e => setForm(f => ({ ...f, license_info: e.target.value }))}
                  placeholder="License key, expiry..."
                  className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
            </div>
            <div className="px-6 py-4 border-t flex justify-end gap-3">
              <button onClick={() => setCreateModal(false)} className="px-4 py-2 text-sm text-slate-600 border rounded-lg hover:bg-slate-50">Cancel</button>
              <button onClick={handleSave} disabled={submitting}
                className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50">
                {submitting ? 'Saving...' : editAsset ? 'Save Changes' : 'Add Asset'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AssetInventory;
